import {
  Button,
  buttonVariants,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@xhubio-saas/core-frontend/ui-components'
import {
  type CatalogCountry,
  type ConversionMode,
  CountrySelector,
  type FormatOption,
  getFormatOptions,
  type SavedTemplate,
  type SitzungsschluesselZustand,
  type TemplateSelection,
} from '@xhubio-saas/invoice-frontend/plugin-invoice-api'
import { FileUp, X } from 'lucide-react'
import { type ReactNode, useId, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { DetectionState } from './bridge'

/** Wert der Option „Automatisch erkennen" im Formatfeld. */
const AUTO_DETECT_VALUE = '__auto-detect__'

/** Die Richtungen in der Reihenfolge der Segmentleiste. */
export const CONVERSION_MODES: readonly ConversionMode[] = ['toPdf', 'toEInvoice', 'toJson']

/** Die Id des Hinweises, auf den der gesperrte Konvertieren-Knopf zeigt (`aria-describedby`). */
export const MISSING_HINT_ID = {
  session: 'invoice-conversion-missing-session',
  country: 'invoice-conversion-missing-country',
  format: 'invoice-conversion-missing-format',
} as const

export type MissingRequirement = keyof typeof MISSING_HINT_ID | 'input' | null

type PackageT = (key: string, params?: Record<string, string>) => string

export interface ConversionOrderProps {
  catalog: CatalogCountry[]
  allowedCountries?: string[]
  country: string | null
  onCountryChange: (country: string) => void
  mode: ConversionMode
  onModeChange: (mode: ConversionMode) => void
  format: FormatOption | null
  onFormatChange: (format: FormatOption) => void
  /** Die App hat eine Erkennung eingespritzt und der Modus liest einen Beleg. */
  autoDetectOffered: boolean
  isAutoDetect: boolean
  onAutoDetect: () => void
  detection: DetectionState | null
  needsTemplate: boolean
  template: TemplateSelection
  /** Dateiname der eigenen Vorlage (nur bei `upload`). */
  templateFileName: string | null
  onTemplateChange: (selection: TemplateSelection, fileName?: string) => void
  savedTemplates?: SavedTemplate[]
  sitzungsschluessel: SitzungsschluesselZustand
  /** Kontingent-Stand nach einer Abweisung (HTTP 429) — nur dann steht die Zeile da. */
  quota?: { current: number; limit: number }
  /** Die erste fehlende Angabe — sie wird an ihrer Zeile genannt. */
  missing: MissingRequirement
  /** `t` im Namensraum `invoiceConversion` (Saetze des Pakets: Laender, Formate, Kontingent). */
  tPackage: PackageT
}

/**
 * **Der Auftrag** — was konvertiert werden soll, als EINE Karte mit Definitionszeilen: Land · Richtung · Format ·
 * Vorlage (· Sitzungsschluessel · Kontingent, nur wenn sie etwas zu sagen haben).
 *
 * 🔴 Eine fehlende Angabe steht an IHRER Zeile, nicht unten am Knopf (Plan 2026-10-10, Erkenntnis aus der
 * Validierung). Der gesperrte Knopf zeigt per `aria-describedby` auf genau diesen Satz.
 *
 * ⚪ Beschriftung links in einer gemeinsamen Spalte ab `sm`, darunter steht sie ueber dem Feld. Die Felder sind auf
 * `max-w-md` begrenzt — ein Auswahlfeld ueber 1 200 px liest sich nicht; die Karte selbst bleibt voll breit.
 */
export function ConversionOrder(props: ConversionOrderProps) {
  const {
    catalog,
    allowedCountries,
    country,
    onCountryChange,
    mode,
    onModeChange,
    tPackage,
    sitzungsschluessel,
    quota,
    missing,
  } = props
  const { t } = useTranslation()

  return (
    <section
      className="rounded-lg border border-border bg-card"
      aria-labelledby="invoice-conversion-order-title"
      data-testid="invoice-conversion-order"
    >
      <h2
        id="invoice-conversion-order-title"
        className="border-b border-border px-4 py-2.5 text-sm font-medium"
      >
        {t('invoiceConversionPage.order.title')}
      </h2>
      <dl className="divide-y divide-border">
        {sitzungsschluessel !== 'da' && (
          <OrderRow id="session" label={t('invoiceConversionPage.order.session')}>
            <p
              id={MISSING_HINT_ID.session}
              className="text-sm text-muted-foreground"
              role="status"
              data-testid="invoice-conversion-session-hint"
            >
              {sitzungsschluessel === 'laedt'
                ? tPackage('sessionKey.loading')
                : tPackage('sessionKey.unavailable')}
            </p>
          </OrderRow>
        )}

        <OrderRow id="country" label={t('invoiceConversionPage.order.country')}>
          <div className="max-w-md">
            <CountrySelector
              value={country}
              onChange={onCountryChange}
              t={tPackage}
              countries={catalog}
              allowedCountries={allowedCountries}
            />
          </div>
          {missing === 'country' && (
            <MissingHint id={MISSING_HINT_ID.country} text={tPackage('actions.missingCountry')} />
          )}
        </OrderRow>

        <OrderRow id="direction" label={t('invoiceConversionPage.order.direction')}>
          {/* Segment wie die Werkbank-Tabs (UI_GUIDELINES §27). Unter `sm` untereinander: „JSON → E-Rechnung" passt
              bei 320 px nicht in ein Drittel, und ein Knopf bricht nie zweizeilig (Hallmark-Gate 49). */}
          <Tabs
            value={mode}
            onValueChange={(value) => onModeChange(value as ConversionMode)}
            data-testid="invoice-mode-toggle"
          >
            <TabsList className="grid h-auto w-full max-w-md grid-cols-1 sm:max-w-xl sm:grid-cols-3">
              {CONVERSION_MODES.map((m) => (
                <TabsTrigger
                  key={m}
                  value={m}
                  className="min-w-0 transition-[background-color,color] motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-ring active:bg-muted data-active:active:bg-primary/90"
                  data-testid={`invoice-mode-${m.toLowerCase()}`}
                >
                  {tPackage(`modes.${m}`)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </OrderRow>

        {mode !== 'toPdf' && <FormatRow {...props} />}

        {props.needsTemplate && <TemplateRow {...props} />}

        {quota && (
          <OrderRow id="quota" label={t('invoiceConversionPage.order.quota')}>
            <div
              className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2"
              data-testid="invoice-quota-exhausted"
            >
              <p className="min-w-0 text-sm">
                <span className="font-medium text-destructive">{tPackage('error.quotaTitle')}</span>{' '}
                <span className="text-muted-foreground tabular-nums">
                  {tPackage('error.quotaBody', {
                    used: String(quota.current),
                    limit: String(quota.limit),
                  })}
                </span>
              </p>
              <Link
                to="/subscription"
                className={buttonVariants({
                  variant: 'outline',
                  size: 'sm',
                  className: 'ml-auto shrink-0 whitespace-nowrap',
                })}
              >
                {tPackage('error.quotaCta')}
              </Link>
            </div>
          </OrderRow>
        )}
      </dl>
    </section>
  )
}

/** Eine Zeile des Auftrags: Beschriftung und Inhalt. Unter `sm` steht die Beschriftung ueber dem Inhalt. */
function OrderRow({ id, label, children }: { id: string; label: string; children: ReactNode }) {
  const labelId = useId()
  return (
    <div
      className="grid grid-cols-1 gap-x-6 gap-y-1.5 px-4 py-3 sm:grid-cols-[9rem_minmax(0,1fr)] sm:items-start"
      data-testid={`invoice-conversion-row-${id}`}
    >
      <dt id={labelId} className="text-sm font-medium text-muted-foreground sm:pt-2">
        {label}
      </dt>
      {/* `<fieldset aria-labelledby>`: die Auswahlfelder des Pakets tragen kein eigenes Label — so nennt der Screenreader
          die Zeile, wenn der Fokus hineinkommt. `min-w-0`: ein fieldset ist sonst mindestens so breit wie sein Inhalt. */}
      <dd className="min-w-0">
        <fieldset className="m-0 min-w-0 space-y-2 border-0 p-0" aria-labelledby={labelId}>
          {children}
        </fieldset>
      </dd>
    </div>
  )
}

/** Der Satz zu einer fehlenden Angabe — an ihrer Zeile. */
function MissingHint({ id, text }: { id: string; text: string }) {
  return (
    <p id={id} className="text-sm text-muted-foreground" data-testid={`${id}`}>
      {text}
    </p>
  )
}

/**
 * Format — Ziel (JSON → E-Rechnung) oder Quelle (E-Rechnung → JSON), aus dem Katalog des Landes
 * (`getFormatOptions`, Paket). Eigenes Feld statt `FormatSelector` des Pakets: dessen Feld hat `min-w-[300px]` (bei
 * 320 px breiter als die Spalte) und zeigt „Automatisch erkennen" mit einem Emoji (Issue an invoice-frontend).
 */
function FormatRow({
  catalog,
  country,
  mode,
  format,
  onFormatChange,
  autoDetectOffered,
  isAutoDetect,
  onAutoDetect,
  detection,
  missing,
  tPackage,
}: ConversionOrderProps) {
  const catalogCountry = country
    ? (catalog.find((c) => c.code.toUpperCase() === country.toUpperCase()) ?? null)
    : null
  const options = catalogCountry ? getFormatOptions(catalogCountry, mode) : []
  const label = mode === 'toEInvoice' ? tPackage('format.target') : tPackage('format.source')
  const optionLabel = (o: FormatOption) =>
    o.isApiDefault ? `${o.label} ${tPackage('format.apiDefault')}` : o.label

  return (
    <OrderRow id="format" label={label}>
      <div className="max-w-md">
        <Select
          // Neu einhaengen je Land: das Primitive behielte sonst seine alte Auswahl (Paket-Erfahrung, FormatSelector).
          key={catalogCountry?.code ?? 'none'}
          value={isAutoDetect ? AUTO_DETECT_VALUE : (format?.id ?? '')}
          onValueChange={(value) => {
            if (value === AUTO_DETECT_VALUE) {
              onAutoDetect()
              return
            }
            const option = options.find((o) => o.id === value)
            if (option) onFormatChange(option)
          }}
          disabled={options.length === 0}
        >
          <SelectTrigger className="w-full" aria-label={label} data-testid="invoice-format-select">
            {isAutoDetect ? (
              <span className="truncate" data-testid="invoice-format-auto-active">
                {format ? optionLabel(format) : tPackage('format.autoDetect')}
              </span>
            ) : format ? (
              <span className="truncate">{optionLabel(format)}</span>
            ) : (
              <SelectValue
                placeholder={
                  !catalogCountry
                    ? tPackage('format.selectCountryFirst')
                    : options.length === 0
                      ? tPackage('format.noFormats')
                      : tPackage('format.selectPlaceholder')
                }
              />
            )}
          </SelectTrigger>
          <SelectContent className="bg-popover">
            {autoDetectOffered && (
              <SelectItem value={AUTO_DETECT_VALUE} data-testid="invoice-format-auto-option">
                {tPackage('format.autoDetect')}
              </SelectItem>
            )}
            {options.map((o) => (
              <SelectItem key={o.id} value={o.id}>
                {optionLabel(o)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Laeuft eine Erkennung, sagt IHR Satz, was fehlt („… bitte von Hand waehlen") — er traegt dann die Id, auf
          die der gesperrte Knopf zeigt. Sonst der Satz zum fehlenden Format. */}
      {isAutoDetect && detection ? (
        <DetectionLine
          detection={detection}
          onPick={onFormatChange}
          tPackage={tPackage}
          hintId={missing === 'format' ? MISSING_HINT_ID.format : undefined}
        />
      ) : (
        missing === 'format' && (
          <MissingHint
            id={MISSING_HINT_ID.format}
            text={
              mode === 'toJson'
                ? tPackage('actions.missingSourceFormat')
                : tPackage('actions.missingFormat')
            }
          />
        )
      )}
    </OrderRow>
  )
}

/** Was die Erkennung sagt — inline unter dem Formatfeld, wie die Erkennung der Validierung. */
function DetectionLine({
  detection,
  onPick,
  tPackage,
  hintId,
}: {
  detection: DetectionState
  onPick: (format: FormatOption) => void
  tPackage: PackageT
  hintId?: string
}) {
  const text: Record<DetectionState['status'], string> = {
    detecting: tPackage('format.detecting'),
    resolved:
      detection.status === 'resolved'
        ? tPackage('format.detected', { format: detection.option.label })
        : '',
    ambiguous:
      detection.status === 'ambiguous'
        ? tPackage('format.detectedAmbiguous', { format: detection.detectedFormat })
        : '',
    notOffered:
      detection.status === 'notOffered'
        ? tPackage('format.detectedNotOffered', { format: detection.detectedFormat })
        : '',
    unrecognized: tPackage('format.detectUnrecognized'),
    failed: tPackage('format.detectFailed'),
  }
  return (
    <div className="space-y-2" role="status" data-testid="invoice-format-detection">
      <p
        id={hintId}
        className="text-sm text-muted-foreground [overflow-wrap:anywhere]"
        data-testid={`invoice-detect-${detection.status}`}
      >
        {text[detection.status]}
      </p>
      {/* 🔴 Mehrdeutig: die Kandidaten stehen zur Wahl, vorbelegt ist nichts. */}
      {detection.status === 'ambiguous' && (
        <div className="flex flex-wrap gap-2">
          {detection.candidates.map((c) => (
            <Button
              key={c.id}
              variant="outline"
              size="sm"
              className="whitespace-nowrap"
              onClick={() => onPick(c)}
              data-testid={`invoice-detect-candidate-${c.id}`}
            >
              {c.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Vorlage (nur JSON → PDF und ZUGFeRD/Factur-X): System-Standard · Beispielvorlage (ohne gespeicherte) · gespeicherte
 * Vorlagen · eigene Datei. „Eigene Vorlage hochladen" ist ein Knopf, kein Textlink (Plan); eigenes Feld statt
 * `TemplateSelector` des Pakets, der den Weg fest als Link rendert.
 */
function TemplateRow({
  template,
  templateFileName,
  onTemplateChange,
  savedTemplates,
  country,
}: ConversionOrderProps) {
  const { t } = useTranslation()
  const fileRef = useRef<HTMLInputElement>(null)
  const label = t('invoiceConversionPage.order.template')
  const saved = [...(savedTemplates ?? [])].sort((a, b) => a.name.localeCompare(b.name))
  const value =
    template.type === 'saved'
      ? `saved:${template.id}`
      : template.type === 'upload'
        ? 'upload'
        : template.type

  return (
    <OrderRow id="template" label={label}>
      <div className="flex flex-wrap items-center gap-2">
        {/* `h-9` an den Knoepfen: so hoch wie das Auswahlfeld daneben (Hallmark-Gate 39). */}
        <div className="min-w-0 flex-[1_1_16rem] sm:max-w-md">
          <Select
            value={value}
            onValueChange={(v) => {
              const next = String(v)
              if (next === 'none' || next === 'default') onTemplateChange({ type: next })
              else if (next.startsWith('saved:'))
                onTemplateChange({ type: 'saved', id: next.slice('saved:'.length) })
            }}
            disabled={country === null}
          >
            <SelectTrigger
              className="w-full"
              aria-label={label}
              data-testid="invoice-template-select"
            >
              <span className="truncate">
                {template.type === 'none'
                  ? t('invoiceConversionPage.template.none')
                  : template.type === 'default'
                    ? t('invoiceConversionPage.template.sample')
                    : template.type === 'upload'
                      ? t('invoiceConversionPage.template.own', { name: templateFileName ?? '' })
                      : (saved.find((s) => s.id === template.id)?.name ?? template.id)}
              </span>
            </SelectTrigger>
            <SelectContent className="bg-popover">
              <SelectItem value="none">{t('invoiceConversionPage.template.none')}</SelectItem>
              {/* Ohne gespeicherte Vorlagen die Beispielvorlage — mit ihnen steht die Systemvorlage in der Liste. */}
              {saved.length === 0 && (
                <SelectItem value="default">
                  {t('invoiceConversionPage.template.sample')}
                </SelectItem>
              )}
              {saved.map((s) => (
                <SelectItem key={s.id} value={`saved:${s.id}`}>
                  {s.name}
                </SelectItem>
              ))}
              {template.type === 'upload' && (
                <SelectItem value="upload" disabled>
                  {t('invoiceConversionPage.template.own', { name: templateFileName ?? '' })}
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
        {template.type === 'upload' ? (
          <Button
            variant="outline"
            className="h-9 shrink-0 whitespace-nowrap"
            onClick={() => onTemplateChange({ type: 'none' })}
            data-testid="invoice-template-remove"
          >
            <X className="h-4 w-4" aria-hidden="true" />
            {t('invoiceConversionPage.template.remove')}
          </Button>
        ) : (
          <Button
            variant="outline"
            className="h-9 shrink-0 whitespace-nowrap"
            disabled={country === null}
            onClick={() => fileRef.current?.click()}
            data-testid="invoice-template-upload"
          >
            <FileUp className="h-4 w-4" aria-hidden="true" />
            {t('invoiceConversionPage.template.upload')}
          </Button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept=".json,application/json"
          className="hidden"
          data-testid="invoice-template-file"
          onChange={async (e) => {
            const file = e.target.files?.[0]
            e.target.value = ''
            if (!file) return
            onTemplateChange({ type: 'upload', content: await file.text() }, file.name)
          }}
        />
      </div>
    </OrderRow>
  )
}
